/** The one workflow the selector lists. Every run here is an allocation engine
 *  run of the Replenishment module; the id survives from the retired V4
 *  orchestrator so the sort helpers below keep one shape. */
export const WORKFLOW_ID = "workflow_replenishment";

export interface RunSelectionRun {
  id: string;
  status: string;
  createdAt: string;
  name?: string | null;
  /** Who started the run, as core's ledger has it: the planner's email, or
   *  `service:<id>` for a run no person started. */
  triggeredBy?: string | null;
  runSettings: {
    workflowId: string;
  };
}

// Only "valid" is ever reported: a read can confirm a run's data is good, but a
// failed read looks the same whether the run is bad or the network blipped.
export type ReceiptValidity = "valid" | "unknown";
export type ReceiptValidityByRunId = Readonly<Record<string, ReceiptValidity>>;

export function parseRunTimestamp(iso: string): number | null {
  const raw = iso.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`;
  const timestamp = new Date(raw).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function compareRunsNewestFirst<Run extends RunSelectionRun>(a: Run, b: Run): number {
  const compareIds = () => (a.id === b.id ? 0 : a.id < b.id ? -1 : 1);
  const aTimestamp = parseRunTimestamp(a.createdAt);
  const bTimestamp = parseRunTimestamp(b.createdAt);
  if (aTimestamp === null && bTimestamp === null) return compareIds();
  if (aTimestamp === null) return 1;
  if (bTimestamp === null) return -1;
  if (aTimestamp !== bTimestamp) return bTimestamp - aTimestamp;
  return compareIds();
}

export function sortWorkflowRuns<Run extends RunSelectionRun>(
  runs: Run[],
  workflowId: string
): Run[] {
  return runs
    .filter((run) => run.runSettings.workflowId === workflowId)
    .sort(compareRunsNewestFirst);
}

export function sortCompletedWorkflowRuns<Run extends RunSelectionRun>(
  runs: Run[],
  workflowId: string,
  completedStatus: string
): Run[] {
  return sortWorkflowRuns(runs, workflowId).filter((run) => run.status === completedStatus);
}

export function findRecommendedRun<Run extends RunSelectionRun>(
  completedRuns: Run[],
  receiptValidityByRunId: ReceiptValidityByRunId
): Run | null {
  return (
    completedRuns.find(
      (run) =>
        receiptValidityByRunId[run.id] === "valid" && parseRunTimestamp(run.createdAt) !== null
    ) ?? null
  );
}

export function resolveSelectedRunId<Run extends RunSelectionRun>(
  selectedRunId: string | null,
  sortedRuns: Run[]
): string | null {
  if (sortedRuns.length === 0) {
    return null;
  }

  if (selectedRunId === null || !sortedRuns.some((run) => run.id === selectedRunId)) {
    return sortedRuns[0].id;
  }

  return selectedRunId;
}

export function resolveSelectedRun<Run extends RunSelectionRun>(
  selectedRunId: string | null,
  sortedRuns: Run[]
): Run | null {
  return sortedRuns.find((run) => run.id === selectedRunId) ?? sortedRuns[0] ?? null;
}

function to12h(hours: number, minutes: number): string {
  const period = hours >= 12 ? "PM" : "AM";
  const h = hours % 12 || 12;
  return `${h}:${String(minutes).padStart(2, "0")} ${period}`;
}

export function formatRunDate(iso: string): string {
  const timestamp = parseRunTimestamp(iso);
  if (timestamp === null) return "Time unavailable";
  const date = new Date(timestamp);
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy} ${to12h(date.getHours(), date.getMinutes())}`;
}

export function formatRunOptionLabel(run: Pick<RunSelectionRun, "createdAt" | "name">): string {
  const date = formatRunDate(run.createdAt);
  const name = run.name?.trim();
  return name ? `${date} — ${name}` : date;
}

/** "9/17/26 12:47 pm" — the stamp a run is picked by; empty when the ledger
 *  carries no usable start. */
export function formatRunStamp(iso: string): string {
  const timestamp = parseRunTimestamp(iso);
  if (timestamp === null) return "";
  const date = new Date(timestamp);
  const yy = String(date.getFullYear() % 100).padStart(2, "0");
  return `${date.getMonth() + 1}/${date.getDate()}/${yy} ${to12h(date.getHours(), date.getMinutes()).toLowerCase()}`;
}

/** A run's label in the pieces the selector shades differently. */
export interface RunSelectorLabelParts {
  /** The run's special tag ("Latest Run"), when it has one — the label's own
   *  words, at full strength. The one tag for now is the newest completed run's. */
  tag: string | null;
  /** The run's number as the label carries it: " (#447)" after a tag, where it
   *  is an aside, or "Run #446" when the number IS the name. Core's
   *  module-scoped run id, the same one `run_id=` carries in the URL and the
   *  engine console shows. */
  number: string;
  /** "9/17/26 12:47 pm"; empty when the ledger carries no usable start. */
  stamp: string;
  /** " by olha" — who started the run, as an aside; empty when no person did. */
  by: string;
}

/** "olha" for "olha@drinklmnt.com": the name a teammate is known by, the way
 *  the settings dialog's version list says it. A run a service started
 *  (`service:<id>`) has no one to name. */
export function runStartedBy(triggeredBy: string | null | undefined): string {
  const actor = (triggeredBy ?? "").trim();
  if (!actor || actor.startsWith("service:")) return "";
  return actor.split("@")[0];
}

export function runSelectorLabelParts(
  run: Pick<RunSelectionRun, "id" | "createdAt" | "triggeredBy">,
  options: { latestRunId?: string | null } = {},
): RunSelectorLabelParts {
  const tag = run.id === options.latestRunId ? "Latest Run" : null;
  const who = runStartedBy(run.triggeredBy);
  return {
    tag,
    number: tag ? ` (#${run.id})` : `Run #${run.id}`,
    stamp: formatRunStamp(run.createdAt),
    by: who ? ` by ${who}` : "",
  };
}

/** Exactly the string the run selector shows for a run —
 *  "Latest Run (#447) - 9/17/26 12:47 pm by olha", "Run #446 - 9/17/26 10:44 am".
 *
 *  Extracted so the dock can report the run the page is on in the planner's own
 *  words (NUC-258). Both the selector and the dock call this (the selector
 *  through the parts above, which join to it), so an answer and the screen can
 *  never describe the same run differently. */
export function runSelectorLabel(
  run: Pick<RunSelectionRun, "id" | "createdAt" | "triggeredBy">,
  options: { latestRunId?: string | null } = {},
): string {
  const { tag, number, stamp, by } = runSelectorLabelParts(run, options);
  return `${tag ?? ""}${number}${stamp ? ` - ${stamp}` : ""}${by}`;
}
